import { reverse } from "@reactivated";

import {
    APIConcreteBundles,
    IConcreteBundle,
    IMattressMatchPreferenceSet,
    IMattressMatchResults,
    IProduct,
    IVerboseProductClass,
    MattressMatchResults,
    Product,
    Products,
    VerboseProductClass,
} from "../models/catalogue";
import {
    IProductAPIURL,
    IProductCategoryID,
    IProductID,
    IProductUUID,
    isoProductAPIURL,
    isoProductCategoryID,
    isoProductID,
    isoProductUUID,
} from "../models/nominals";
import { APIPrice, IAPIPrice } from "../models/prices";
import {
    IProductShippingMethod,
    ProductShippingMethods,
} from "../models/shipping";
import { check } from "../models/utils";
import { CSRF_HEADER, ajax, getCSRFToken } from "../utils/ajax";
import { PromiseMutex } from "../utils/mutex";

export const getProductURL = (productID: IProductID): IProductAPIURL => {
    const unwrappedProductID = isoProductID.unwrap(productID);
    return isoProductAPIURL.wrap(
        reverse("product-detail", { pk: unwrappedProductID }),
    );
};

export const getProductID = (productURL: IProductAPIURL): IProductID | null => {
    const groups = isoProductAPIURL.unwrap(productURL).match(/\/(\d+)\/$/);
    return groups ? isoProductID.wrap(parseInt(groups[1], 10)) : null;
};

export const getProductShippingMethods = async (
    productID: IProductID,
    postCode: string,
): Promise<ReadonlyArray<IProductShippingMethod>> => {
    const unwrappedProductID = isoProductID.unwrap(productID);
    const mutex = new PromiseMutex<ReadonlyArray<IProductShippingMethod>>(
        `product-shipping-methods-get-${unwrappedProductID}`,
    );
    let loading = mutex.getPromise();
    if (!loading) {
        loading = ajax
            .get(
                reverse("product-shipping-methods", { pk: unwrappedProductID }),
            )
            .query({ postcode: postCode })
            .set("Accept", "application/json")
            .then((resp) => {
                return check(ProductShippingMethods.decode(resp.body));
            });
        mutex.setPromise(loading);
    }
    return loading;
};

export const getProduct = async (
    productURL: IProductAPIURL,
): Promise<IProduct> => {
    const unwrappedProductURL = isoProductAPIURL.unwrap(productURL);
    const mutex = new PromiseMutex<IProduct>(
        `product-get-${unwrappedProductURL}`,
    );
    let loading = mutex.getPromise();
    if (!loading) {
        loading = ajax
            .get(unwrappedProductURL)
            .set("Accept", "application/json")
            .then((resp) => {
                return check(Product.decode(resp.body));
            });
        mutex.setPromise(loading);
    }
    return loading;
};

export const getProductPrice = async (
    productURL: IProductAPIURL,
    quantity: number,
): Promise<IAPIPrice> => {
    const unwrappedProductURL = isoProductAPIURL.unwrap(productURL);
    const mutex = new PromiseMutex<IAPIPrice>(
        `product-price-get-${unwrappedProductURL}`,
    );
    let loading = mutex.getPromise();
    if (!loading) {
        loading = ajax
            .get(`${unwrappedProductURL}price/`)
            .query({
                quantity: quantity,
            })
            .set("Accept", "application/json")
            .then((resp) => {
                return check(APIPrice.decode(resp.body));
            });
        mutex.setPromise(loading);
    }
    return loading;
};

export const loadCategory = async (
    categoryID: IProductCategoryID,
): Promise<ReadonlyArray<IProduct>> => {
    const unwrappedCatID = isoProductCategoryID.unwrap(categoryID);
    const mutex = new PromiseMutex<ReadonlyArray<IProduct>>(
        `products-category-list-${unwrappedCatID}`,
    );
    let loading = mutex.getPromise();
    if (!loading) {
        loading = ajax
            .get(reverse("category-products", { pk: unwrappedCatID }))
            .set("Accept", "application/json")
            .then((resp) => {
                return check(Products.decode(resp.body));
            });
        mutex.setPromise(loading);
    }
    return loading;
};

export const listConcreteBundles = async (
    productURL: IProductAPIURL,
): Promise<IConcreteBundle[]> => {
    const mutex = new PromiseMutex<IConcreteBundle[]>(
        `product-concrete-bundle-list-${productURL}`,
    );
    let loading = mutex.getPromise();
    if (!loading) {
        loading = ajax
            .get(`${productURL}concretebundles/`)
            .set("Accept", "application/json")
            .then((resp) => {
                return check(APIConcreteBundles.decode(resp.body));
            })
            .then((bundles) => {
                const loadingSuggestedProducts = bundles.map(
                    async (inputBundle) => {
                        // Load the details of each suggested product
                        const products = await Promise.all(
                            inputBundle.suggested_products.map((pID) => {
                                return getProduct(getProductURL(pID));
                            }),
                        );
                        // Filter out product which are out of stock
                        const availableProducts = products.filter(
                            (p) => p.availability.is_available_to_buy,
                        );
                        // Inject the remaining product details into the bundle data
                        const bundle: IConcreteBundle = {
                            ...inputBundle,
                            suggested_products: availableProducts,
                        };
                        return bundle;
                    },
                );
                return Promise.all(loadingSuggestedProducts);
            });
        mutex.setPromise(loading);
    }
    return loading;
};

export const getProductByUUID = async (
    productUUID: IProductUUID,
): Promise<IProduct | null> => {
    const UUIDRegex =
        /^[0-9a-f]{8}\b-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-\b[0-9a-f]{12}$/;
    if (!productUUID || !isoProductUUID.unwrap(productUUID).match(UUIDRegex)) {
        return null;
    }
    const mutex = new PromiseMutex<IProduct | null>(
        `product-get-${productUUID}`,
    );
    let loading = mutex.getPromise();
    if (!loading) {
        loading = ajax
            .get(reverse("product-list") + `?core_product__uuid=${productUUID}`)
            .set("Accept", "application/json")
            .then((resp) => {
                if (!resp.body || !resp.body[0]) {
                    return null;
                }
                const product: IProduct = resp.body[0];
                const url = getProductURL(product.id);
                return getProduct(url);
            });
        mutex.setPromise(loading);
    }
    return loading;
};

export const getProductClass = async (
    productClassSlug: string,
): Promise<IVerboseProductClass> => {
    const mutex = new PromiseMutex<IVerboseProductClass>(
        `product-class-${productClassSlug}`,
    );
    let loading = mutex.getPromise();
    if (!loading) {
        loading = ajax
            .get(reverse("productclass-detail", { slug: productClassSlug }))
            .set("Accept", "application/json")
            .then((resp) => {
                return check(VerboseProductClass.decode(resp.body));
            });
        mutex.setPromise(loading);
    }
    return loading;
};

export const submitMattressMatchPreferences = async (
    data: IMattressMatchPreferenceSet,
): Promise<IMattressMatchResults> => {
    return ajax
        .post(reverse("mattress-match"))
        .set("Accept", "application/json")
        .set(CSRF_HEADER, await getCSRFToken())
        .send(data)
        .then((resp): IMattressMatchResults => {
            return check(MattressMatchResults.decode(resp.body));
        });
};
